// ─── DESIGN TOKENS ─────────────────────────────────────────────────────────
// Import this in App.js:  import { T, C } from './styles';
// Then replace all hardcoded colors with T.* and style objects with C.*

export const T = {
  // Brand colors
  teal:      "#1A6B58",
  tealLight: "#2BAD94",
  teal10:    "rgba(43,173,148,.1)",
  teal20:    "rgba(43,173,148,.2)",
  coral:     "#E8728A",
  coral10:   "rgba(232,114,138,.1)",
  purple:    "#9D78C7",
  purple10:  "rgba(157,120,199,.1)",
  amber:     "#E8A54A",
  amber10:   "rgba(232,165,74,.1)",
  green:     "#34D399",
  green10:   "rgba(52,211,153,.1)",
  blue:      "#60A5FA",
  blue10:    "rgba(96,165,250,.1)",

  // Neutrals
  cream:     "#F5EFE8",
  creamD:    "#EDE6DC",
  creamDD:   "#E2D9CE",
  white:     "#FFFFFF",
  dark:      "#18201F",
  mid:       "#6B7A78",
  light:     "#A8B5B2",
  lighter:   "#D4DCD9",

  // Typography
  fontDisplay: "'Cormorant Garamond', Georgia, serif",
  fontUI:      "'DM Sans', system-ui, -apple-system, sans-serif",

  // Shadows
  shadowSm:  "0 2px 10px rgba(0,0,0,.055)",
  shadowMd:  "0 4px 20px rgba(0,0,0,.08)",
  shadowLg:  "0 8px 40px rgba(0,0,0,.12)",

  // Radii
  rSm: 10, rMd: 16, rLg: 22,
};

// ─── COMPONENT STYLES ────────────────────────────────────────────────────────
export const C = {
  // ── Layout ────────────────────────────────────────────────────────────────
  root: {
    display:"flex", minHeight:"100vh",
    fontFamily: T.fontUI,
    background: T.cream,
    color: T.dark,
  },
  sidebar: {
    width: 220,
    background: T.dark,
    display: "flex",
    flexDirection: "column",
    padding: "20px 10px 14px",
    gap: 3,
    flexShrink: 0,
  },
  logo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 8px 18px",
    borderBottom: `1px solid rgba(255,255,255,.08)`,
    marginBottom: 6,
  },
  nav: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "9px 12px",
    borderRadius: T.rSm,
    border: "none",
    background: "transparent",
    color: T.light,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
    textAlign: "left",
    fontFamily: T.fontUI,
    transition: "all .15s",
  },
  navOn: {
    background: T.teal10,
    color: T.teal,
    fontWeight: 700,
  },
  sideFoot: {
    marginTop: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    paddingTop: 13,
    borderTop: `1px solid rgba(255,255,255,.07)`,
  },
  toast: {
    background: T.teal10,
    color: T.teal,
    fontSize: 11,
    fontWeight: 600,
    padding: "5px 10px",
    borderRadius: 20,
    textAlign: "center",
    border: `1px solid ${T.teal20}`,
  },
  main: {
    flex: 1,
    padding: "28px 32px",
    overflowY: "auto",
  },

  // ── Cards / Grid ──────────────────────────────────────────────────────────
  card: {
    background: T.white,
    borderRadius: T.rLg,
    boxShadow: "0 2px 10px rgba(0,0,0,.055)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
    gap: 12,
  },
  statRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))",
    gap: 12,
    marginBottom: 28,
  },
  alertChip: {
    background: T.amber10,
    color: T.amber,
    padding: "4px 12px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    border: `1px solid ${T.amber}40`,
  },

  // ── Filters / Pills ───────────────────────────────────────────────────────
  filters: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  fBtn: {
    background: T.white,
    border: `1px solid ${T.creamD}`,
    borderRadius: 100,
    padding: "5px 14px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
    color: T.mid,
    fontFamily: T.fontUI,
    transition: "all .15s",
  },
  fOn: {
    background: T.dark,
    color: T.white,
    border: `1px solid ${T.dark}`,
    fontWeight: 700,
    boxShadow: "0 3px 10px rgba(0,0,0,.18)",
  },

  // ── Search ────────────────────────────────────────────────────────────────
  searchWrap: {
    background: T.white,
    borderRadius: 14,
    padding: "11px 15px",
    display: "flex",
    alignItems: "center",
    gap: 9,
    boxShadow: "0 2px 10px rgba(0,0,0,.055)",
    marginBottom: 14,
  },
  searchInp: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 14,
    color: T.dark,
    flex: 1,
    fontFamily: T.fontUI,
  },

  // ── Table ─────────────────────────────────────────────────────────────────
  tblWrap: {
    background: T.white,
    borderRadius: T.rLg,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.055)",
  },
  tbl: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tr: {
    borderTop: `1px solid ${T.cream}`,
    transition: "background .1s",
  },
  td: {
    padding: "11px 14px",
    fontSize: 13,
    color: T.dark,
  },

  // ── Buttons ───────────────────────────────────────────────────────────────
  tealBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: T.teal,
    color: T.white,
    border: "none",
    borderRadius: 12,
    padding: "9px 16px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: T.fontUI,
    transition: "opacity .15s",
  },
  editBtn: {
    background: T.teal10,
    border: `1px solid ${T.teal20}`,
    borderRadius: T.rSm,
    padding: "5px 12px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    color: T.teal,
    fontFamily: T.fontUI,
    transition: "all .15s",
  },
  ghostBtn: {
    background: T.white,
    border: `1px solid ${T.creamD}`,
    borderRadius: T.rSm,
    padding: "6px 13px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    color: T.mid,
    fontFamily: T.fontUI,
  },
  iconBtn: {
    background: T.teal10,
    border: `1px solid ${T.teal20}`,
    borderRadius: T.rSm,
    padding: "5px 7px",
    cursor: "pointer",
    color: T.teal,
    display: "flex",
    alignItems: "center",
  },
  cancelBtn: {
    background: T.cream,
    color: T.mid,
    border: "none",
    borderRadius: T.rSm,
    padding: "8px 15px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: T.fontUI,
  },
  delBtn: {
    background: "rgba(248,113,113,.08)",
    color: "#f87171",
    border: "1px solid rgba(248,113,113,.2)",
    borderRadius: T.rSm,
    padding: "8px 15px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    marginRight: "auto",
    fontFamily: T.fontUI,
  },
  xBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: T.mid,
    display: "flex",
    alignItems: "center",
    padding: 4,
    borderRadius: 6,
  },

  // ── Modals ────────────────────────────────────────────────────────────────
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
  },
  mbox: {
    background: T.white,
    borderRadius: T.rLg,
    width: "100%",
    maxWidth: 520,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 8px 40px rgba(0,0,0,.12)",
  },
  mhead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 20px",
    borderBottom: `1px solid ${T.creamD}`,
    position: "sticky",
    top: 0,
    background: T.white,
    zIndex: 1,
    borderRadius: `${T.rLg}px ${T.rLg}px 0 0`,
  },

  // ── Form inputs ───────────────────────────────────────────────────────────
  inp: {
    width: "100%",
    padding: "8px 11px",
    borderRadius: T.rSm,
    border: `1px solid ${T.creamD}`,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: T.fontUI,
    color: T.dark,
    background: T.white,
    transition: "border-color .15s",
  },

  // ── Calendar nav ──────────────────────────────────────────────────────────
  calNavBtn: {
    background: T.white,
    border: `1px solid ${T.creamD}`,
    borderRadius: T.rSm,
    padding: "5px 15px",
    cursor: "pointer",
    fontSize: 18,
    color: T.teal,
    fontWeight: 700,
    lineHeight: 1,
  },

  // ── Misc ──────────────────────────────────────────────────────────────────
  langBdg: {
    background: T.teal10,
    padding: "2px 9px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    color: T.teal,
  },
  catBdg: {
    background: T.amber10,
    padding: "2px 9px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    color: T.amber,
  },
  search: {
    width: "100%",
    maxWidth: 300,
    padding: "8px 13px",
    borderRadius: T.rSm,
    border: `1px solid ${T.creamD}`,
    fontSize: 13,
    marginBottom: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: T.fontUI,
  },
};

// ─── SHARED UI COMPONENTS ─────────────────────────────────────────────────────
// Replace all existing PH, SH, BS, FL, TH, Dim, Bdg, PC, TR, MA, ES with these

// Page Header
export function PH({title, sub, action}) {
  return (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:26}}>
      <div>
        <h1 style={{margin:0,fontSize:30,fontWeight:700,color:T.dark,fontFamily:T.fontDisplay,letterSpacing:"-.3px",lineHeight:1.1}}>{title}</h1>
        {sub && <p style={{margin:"4px 0 0",fontSize:12,color:T.mid}}>{sub}</p>}
      </div>
      {action && (
        <button style={C.tealBtn} onClick={action.fn}>
          <span style={{fontSize:16,lineHeight:1}}>＋</span>
          <span>{action.lbl}</span>
        </button>
      )}
    </div>
  );
}

// Section Header
export function SH({children, onClick}) {
  return (
    <h2 onClick={onClick} style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"1px",margin:"22px 0 10px",cursor:onClick?"pointer":"default",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <span>{children}</span>
      {onClick && <span style={{fontSize:14,color:T.teal,fontWeight:400,letterSpacing:0}}>See all →</span>}
    </h2>
  );
}

// Bold Section (used inside modals)
export function BS({children}) {
  return (
    <div style={{fontSize:10,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:".8px",borderBottom:`1px solid ${T.creamD}`,paddingBottom:5,marginBottom:10,marginTop:4}}>
      {children}
    </div>
  );
}

// Form Label
export function FL({lbl, children}) {
  return (
    <div style={{marginBottom:11}}>
      <label style={{display:"block",fontSize:10,fontWeight:700,color:T.mid,marginBottom:4,textTransform:"uppercase",letterSpacing:".5px"}}>
        {lbl}
      </label>
      {children}
    </div>
  );
}

// Table Header cell
export function TH({children}) {
  return (
    <th style={{background:T.cream,padding:"10px 14px",textAlign:"left",fontSize:10,fontWeight:700,color:T.mid,textTransform:"uppercase",letterSpacing:".5px"}}>
      {children}
    </th>
  );
}

// Dim / empty text
export function Dim({children}) {
  return (
    <div style={{color:T.lighter,fontSize:13,padding:"16px 4px"}}>
      {children}
    </div>
  );
}

// Status Badge
export function Bdg({cfg, children}) {
  return (
    <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:100,background:cfg.bg,color:cfg.color,display:"inline-block",whiteSpace:"nowrap"}}>
      {children}
    </span>
  );
}

// Project Card
export function PC({p, clients, onClick}) {
  const cl  = clients.find(c => c.id === p.clientId);
  const cf  = {inquiry:{color:T.purple,bg:T.purple10},booked:{color:T.blue,bg:T.blue10},"in-progress":{color:T.amber,bg:T.amber10},complete:{color:T.green,bg:T.green10}}[p.status] || {color:T.purple,bg:T.purple10};
  return (
    <div style={{...C.card,cursor:"pointer",padding:15,transition:"transform .15s",overflow:"hidden",position:"relative"}} onClick={onClick}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=T.shadowMd;}}
      onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="";}}>
      <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
        <Bdg cfg={cf}>{cf.label || p.status}</Bdg>
        <span style={{fontSize:10,fontWeight:700,color:T.coral,background:T.coral10,padding:"2px 8px",borderRadius:20}}>{p.type}</span>
      </div>
      <div style={{fontWeight:700,fontSize:14,marginBottom:4,color:T.dark}}>{p.title}</div>
      <div style={{fontSize:12,color:T.mid}}>📅 {p.date||"—"} · 📍 {p.venue||"—"}</div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:10,paddingTop:8,borderTop:`1px solid ${T.creamD}`,fontSize:12}}>
        <span style={{color:T.mid,fontWeight:500}}>{cl?.name||"—"}</span>
        <span style={{color:T.green,fontWeight:700,fontFamily:T.fontDisplay,fontSize:14}}>${Number(p.total||0).toFixed(2)}</span>
      </div>
    </div>
  );
}

// Totals Row (invoice builder)
export function TR({l, v, bold, big, dim, clr}) {
  return (
    <div style={{display:"flex",justifyContent:"space-between",padding:big?"6px 0 0":"2px 0",fontSize:big?14:12,fontWeight:bold?800:400,color:clr||(dim?T.lighter:T.dark),borderTop:big?`2px solid ${T.creamDD}`:undefined,marginTop:big?5:0}}>
      <span>{l}</span><span>{v}</span>
    </div>
  );
}

// Modal Actions
export function MA({onSave, onDel, onX}) {
  return (
    <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16,borderTop:`1px solid ${T.creamD}`,paddingTop:13}}>
      {onDel && <button style={C.delBtn} onClick={onDel}>Delete</button>}
      <button style={C.cancelBtn} onClick={onX}>Cancel</button>
      <button style={C.tealBtn} onClick={onSave}>Save · Guardar</button>
    </div>
  );
}

// Empty State
export function ES({emoji, title, sub, action}) {
  return (
    <div style={{background:T.white,borderRadius:T.rLg,padding:"44px 28px",display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",boxShadow:C.card.boxShadow}}>
      <div style={{fontSize:48,marginBottom:14}}>{emoji}</div>
      <div style={{fontSize:17,fontWeight:700,color:T.dark,marginBottom:6}}>{title}</div>
      <div style={{fontSize:13,color:T.light,lineHeight:1.6}}>{sub}</div>
      {action && <button style={{...C.tealBtn,marginTop:20,justifyContent:"center"}} onClick={action.fn}>{action.lbl}</button>}
    </div>
  );
}
